import { __assign, __spreadArray } from '../node_modules/tslib/tslib.es6.js';
import _get from 'lodash/get';
import _orderBy from 'lodash/orderBy';
import { CampaignType } from '../types/Campaign.js';
import { CheckoutEligibility } from '../types/product.js';
import { escapeUriPath } from './stringUtils.js';

/**
 * @returns rewards associated with a product & brand
 * - used in product bullet points widget
 * - return rewards only if it's > 0
 */
var getProductPageRewards = function (_a) {
    var productEntity = _a.productEntity, brandEntity = _a.brandEntity, brandclub = _a.brandclub, retailers = _a.retailers;
    var rewards = {};
    var sortOrder = [
        CampaignType.DailyDealsRewardCampaign,
        CampaignType.PurchaseRewardCampaign,
        CampaignType.MembershipRewardCampaign,
        CampaignType.SurveyRewardCampaign,
        CampaignType.AffiliateRewardCampaign,
        CampaignType.ContentRewardCampaign,
        CampaignType.ReferralRewardCampaign,
        CampaignType.RetailerConnectRewardCampaign,
    ];
    var rewardTypeToCampaignMapping = {
        join: CampaignType.MembershipRewardCampaign,
        survey: CampaignType.SurveyRewardCampaign,
        content: CampaignType.ContentRewardCampaign,
        customerReferral: CampaignType.ReferralRewardCampaign,
    };
    // product rewards
    if (Array.isArray(productEntity === null || productEntity === void 0 ? void 0 : productEntity.campaignRewards)) {
        for (var _i = 0, _b = productEntity.campaignRewards; _i < _b.length; _i++) {
            var campaignReward = _b[_i];
            if (typeof (campaignReward === null || campaignReward === void 0 ? void 0 : campaignReward.rewardAmount) === 'number' && typeof (campaignReward === null || campaignReward === void 0 ? void 0 : campaignReward.campaignType) === 'string') {
                rewards[campaignReward.campaignType] = Math.max((rewards === null || rewards === void 0 ? void 0 : rewards[campaignReward.campaignType]) || 0, campaignReward.rewardAmount);
            }
        }
    }
    // brand rewards
    if (typeof (brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.rewardAmounts) === 'object') {
        var rewardAmounts = Object.entries(brandEntity.rewardAmounts);
        for (var _c = 0, rewardAmounts_1 = rewardAmounts; _c < rewardAmounts_1.length; _c++) {
            var _d = rewardAmounts_1[_c], key = _d[0], value = _d[1];
            if (Object.keys(rewardTypeToCampaignMapping).includes(key) && typeof (value === null || value === void 0 ? void 0 : value.rewardAmount) === 'number') {
                rewards[rewardTypeToCampaignMapping[key]] = value.rewardAmount;
            }
        }
    }
    // brandclub reward
    if (typeof (brandclub === null || brandclub === void 0 ? void 0 : brandclub.availableSurveyRewardAmount) === 'number') {
        rewards[CampaignType.SurveyRewardCampaign] = brandclub.availableSurveyRewardAmount;
    }
    // retailer connect reward
    var connectRewardAmount = (retailers || []).reduce(function (acc, retailer) { var _a; return acc + ((_a = retailer === null || retailer === void 0 ? void 0 : retailer.retailerConnectRewardAmount) !== null && _a !== void 0 ? _a : 0); }, 0);
    if (connectRewardAmount) {
        rewards[CampaignType.RetailerConnectRewardCampaign] = connectRewardAmount;
    }
    return sortOrder
        .filter(function (rewardType) { return !!(rewards === null || rewards === void 0 ? void 0 : rewards[rewardType]); })
        .map(function (rewardType) { return ({
        rewardType: rewardType,
        rewardAmount: rewards[rewardType],
    }); });
};
var buildRewardList = function (rewards, brandclub, customerSpendByBrand) {
    var _a;
    var mapList = [
        {
            id: CampaignType.PurchaseRewardCampaign,
            amount: 0,
            subTitle: 'for purchasing product',
        },
        {
            id: CampaignType.DailyDealsRewardCampaign,
            amount: 0,
            subTitle: 'for instant purchase',
        },
    ];
    if (customerSpendByBrand && brandclub && customerSpendByBrand.brandclubIds.indexOf(brandclub.brandclubId) >= 0) {
        mapList.push({
            id: CampaignType.SurveyRewardCampaign,
            amount: 0,
            subTitle: 'for completing surveys',
        });
    }
    mapList.push({
        id: CampaignType.AffiliateRewardCampaign,
        amount: 0,
        subTitle: 'affiliate commission',
    });
    mapList.push({
        id: CampaignType.ContentRewardCampaign,
        amount: 0,
        subTitle: 'in content rewards',
    });
    var res = [];
    var maxByType = {};
    rewards
        .filter(function (_a) {
        var campaignStatus = _a.campaignStatus;
        return campaignStatus === 'live';
    })
        .forEach(function (rewardBase) {
        // We want to blend purchase and limitedTimePurchaseRewardCampaign
        var reward = __assign({}, rewardBase);
        var rewardFound = mapList.find(function (item) { return item.id === reward.campaignType; });
        if (rewardFound) {
            if (maxByType[reward.campaignType]) {
                // if this reward amount is higher, replace
                if (reward.rewardAmount > maxByType[reward.campaignType].amount) {
                    maxByType[reward.campaignType] = __assign(__assign({}, rewardFound), { amount: reward.rewardAmount });
                }
            }
            else {
                maxByType[reward.campaignType] = __assign(__assign({}, rewardFound), { amount: reward.rewardAmount });
            }
        }
    });
    if (brandclub) {
        var surveyAmount = _get(brandclub, ['availableSurveyRewardAmount'], 0);
        var contentAmount = _get(brandclub, ['availableContentRewardAmount'], 0);
        var joinClubReward = (_a = brandclub === null || brandclub === void 0 ? void 0 : brandclub.campaignRewards) === null || _a === void 0 ? void 0 : _a.find(function (c) { return c.campaignType === CampaignType.MembershipRewardCampaign; });
        if (joinClubReward && !brandclub.isMember) {
            maxByType.promoteBrandClubRewardCampaign = {
                id: CampaignType.MembershipRewardCampaign,
                amount: joinClubReward.rewardAmount,
                subTitle: 'for joining the club',
            };
        }
        if (surveyAmount) {
            maxByType.surveyRewardCampaign = {
                id: CampaignType.SurveyRewardCampaign,
                amount: surveyAmount,
                subTitle: 'for completing surveys',
            };
        }
        if (contentAmount) {
            maxByType.contentRewardCampaign = {
                id: CampaignType.ContentRewardCampaign,
                amount: contentAmount,
                subTitle: 'in content rewards',
            };
        }
    }
    res = _orderBy(Object.values(maxByType), ['amount'], ['desc']);
    return res;
};
var getRewardTextFromCampaignType = function (campaignType) {
    switch (campaignType) {
        case CampaignType.DailyDealsRewardCampaign:
            return 'Daily Deals reward';
        case CampaignType.PurchaseRewardCampaign:
            return 'Purchase reward';
        case CampaignType.MembershipRewardCampaign:
            return 'Membership reward';
        case CampaignType.SurveyRewardCampaign:
            return 'Survey reward';
        case CampaignType.AffiliateRewardCampaign:
            return 'Affiliate reward';
        case CampaignType.ContentRewardCampaign:
            return 'Content reward';
        case CampaignType.ReferralRewardCampaign:
            return 'Referral reward';
        case CampaignType.RetailerConnectRewardCampaign:
            return 'Connect reward';
        default:
            return '';
    }
};
var getPurchaseRewardAmountForProduct = function (product) {
    var _a, _b;
    if (((_a = product.scores) === null || _a === void 0 ? void 0 : _a.maxPurchaseRewardPercent) && product.retailPrice) {
        return +((product.scores.maxPurchaseRewardPercent / 100) * product.retailPrice).toFixed(2);
    }
    // Base case: just purchase reward amount is set
    return ((_b = product.scores) === null || _b === void 0 ? void 0 : _b.maxPurchaseRewardAmount) || product.totalRewardAmount || 0;
};
var isProductEligibleForCheckout = function (product, checkoutAllowedOfferType) {
    var isOutOfStock = isProductOutOfStock(product, checkoutAllowedOfferType);
    return !isOutOfStock && isServerSideCheckoutEligible(product.checkoutEligibility);
};
var isProductOutOfStock = function (product, checkoutAllowedOfferType) {
    var isOutOfStock = !product.isAvailable;
    if (checkoutAllowedOfferType === 'firstPartyOnly' && !product.hasFirstPartyOffer) {
        isOutOfStock = true;
    }
    else if (product.offers && product.offers.length > 0) {
        isOutOfStock = product.offers.every(function (offer) { return offer.availabilityStatus === 'OutOfStock'; });
    }
    return isOutOfStock;
};
var isServerSideCheckoutEligible = function (checkoutEligibility) {
    return checkoutEligibility === CheckoutEligibility.SERVER_SIDE;
};
var getAddToBagText = function (product, checkoutAllowedOfferType) {
    return isProductEligibleForCheckout(product, checkoutAllowedOfferType) ? '+ Add' : 'View';
};
var getProductUrl = function (params) {
    var _a;
    if (params.storeType === 'CustomDTCStore' && !params.dtcProductPageUrl) {
        return "#";
    }
    if (params.dtcProductPageUrl) {
        return params.dtcProductPageUrl;
    }
    return "/".concat(escapeUriPath((_a = params.title) === null || _a === void 0 ? void 0 : _a.substring(0, 40), '-'), "/p/").concat(params.stacklineSku, "/product?").concat(params.urlSearchParams.toString());
};
var isInstantPurchaseRewardAvailable = function (reward) {
    var isInstantPurchase = reward.campaignType === CampaignType.DailyDealsRewardCampaign ||
        (reward.rewardUnlockStrategy === 'instant' && reward.campaignType === CampaignType.PurchaseRewardCampaign);
    var isLive = reward.campaignStatus === 'live';
    return isInstantPurchase && isLive;
};
var isPurchaseRewardAvailable = function (reward) {
    var isPurchase = reward.campaignType === CampaignType.PurchaseRewardCampaign;
    var isLive = reward.campaignStatus === 'live';
    return isPurchase && isLive;
};
var getPurchaseRewardAmountForProductV2 = function (p) {
    var _a, _b;
    var rewardType = 'purchase';
    var rewardAmount = 0;
    var rewardPercent = 0;
    var liveInstantPurchase = (_a = p === null || p === void 0 ? void 0 : p.campaignRewards) === null || _a === void 0 ? void 0 : _a.filter(function (reward) {
        return isInstantPurchaseRewardAvailable(reward);
    });
    if (liveInstantPurchase && liveInstantPurchase.length > 0) {
        var maxInstantPurchaseReward = liveInstantPurchase.reduce(function (prev, current) {
            if (current.rewardAmount > prev.rewardAmount) {
                return current;
            }
            return prev;
        });
        rewardType = 'instant';
        rewardAmount = maxInstantPurchaseReward.rewardAmount;
        rewardPercent = maxInstantPurchaseReward.rewardPercent || 0;
    }
    else {
        var livePurchase = (_b = p === null || p === void 0 ? void 0 : p.campaignRewards) === null || _b === void 0 ? void 0 : _b.filter(function (reward) {
            return isPurchaseRewardAvailable(reward);
        });
        if (livePurchase && livePurchase.length > 0) {
            var maxPurchaseReward = livePurchase.reduce(function (prev, current) {
                if (current.rewardAmount > prev.rewardAmount) {
                    return current;
                }
                return prev;
            });
            rewardAmount = maxPurchaseReward.rewardAmount;
            rewardPercent = maxPurchaseReward.rewardPercent || 0;
        }
    }
    return {
        type: rewardType,
        amount: rewardAmount,
        percent: rewardPercent,
    };
};
var getSortedBuyBoxProducts = function (productsToList) {
    if (!productsToList)
        return [];
    // Sort products by Add > Price > Rewards > push Out of Stock to bottom
    var sortedProducts = __spreadArray([], productsToList, true).sort(function (a, b) {
        if (isProductOutOfStock(a) && !isProductOutOfStock(b))
            return 1;
        if (!isProductOutOfStock(a) && isProductOutOfStock(b))
            return -1;
        // check server only so that it is sync between web app and mobile app
        if (a.checkoutEligibility === CheckoutEligibility.SERVER_SIDE &&
            b.checkoutEligibility !== CheckoutEligibility.SERVER_SIDE)
            return -1;
        if (a.checkoutEligibility !== CheckoutEligibility.SERVER_SIDE &&
            b.checkoutEligibility === CheckoutEligibility.SERVER_SIDE)
            return 1;
        if (a.retailPrice && b.retailPrice) {
            var aReward = getPurchaseRewardAmountForProductV2(a);
            var bReward = getPurchaseRewardAmountForProductV2(b);
            if (a.retailPrice - aReward.amount < b.retailPrice - bReward.amount)
                return -1;
            if (a.retailPrice - aReward.amount > b.retailPrice - bReward.amount)
                return 1;
        }
        if (a.campaignRewards && b.campaignRewards) {
            var aReward = getPurchaseRewardAmountForProductV2(a);
            var bReward = getPurchaseRewardAmountForProductV2(b);
            if (aReward.amount > bReward.amount)
                return -1;
            if (aReward.amount < bReward.amount)
                return 1;
        }
        return 0;
    });
    return sortedProducts;
};

export { buildRewardList, getAddToBagText, getProductPageRewards, getProductUrl, getPurchaseRewardAmountForProduct, getPurchaseRewardAmountForProductV2, getRewardTextFromCampaignType, getSortedBuyBoxProducts, isProductEligibleForCheckout, isProductOutOfStock, isServerSideCheckoutEligible };
//# sourceMappingURL=productUtils.js.map
